import { tidy, rename, replaceNully, groupBy, count } from '@tidyjs/tidy';

/**
 * Extract numeric values from a dataset.
 * 
 * @version 1.0
 * 
 * @param {Array} data - Dataset to extract values from.
 * @param {string} key - Key to extract the numeric value from each object.
 * @returns {Array} Array of extracted numeric values.
 */
function extractValues(data, key) {
  return data
    .map((item) => parseFloat(item[key]))
    .filter((value) => !isNaN(value));
}

/**
 * Calculate the average of numeric values in a dataset.
 * 
 * @version 1.0
 * 
 * @param {Array} data - Dataset to calculate the average from.
 * @param {string} key - Key to extract the numeric value from each object.
 * @returns {number} Average value.
 */
function calculateAverage(data, key) {
  const values = extractValues(data, key);
  if (values.length === 0) return 0;
  return values.reduce((total, score) => total + score, 0) / values.length;
}

/**
 * Calculate the median of numeric values in a dataset.
 * 
 * @version 1.0
 * 
 * @param {Array} data - Dataset to calculate the median from.
 * @param {string} key - Key to extract the numeric value from each object.
 * @returns {number} Median value.
 */
function calculateMedian(data, key) {
  const values = extractValues(data, key);
  if (values.length === 0) return 0;

  const sortedValues = [...values].sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedValues.length / 2);

  return sortedValues.length % 2 === 0
    ? (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2
    : sortedValues[middleIndex];
}

/**
 * Calculate percentages for an array of numbers.
 *
 * @version 1.0
 * 
 * @param {number[]} data - Array of numbers.
 * @returns {number[]} Array of percentage values.
 */
const calculatePercentages = (data) => {
  const total = data.reduce((sum, count) => sum + count, 0);
  return data.map((count) => ((count / total) * 100).toFixed(2));
};

/**
 * Process data and perform group calculations based on optional variables.
 *
 * @version 1.0
 * 
 * @param {Array} data - Dataset to be processed.
 * @param {Array} [optionalVariables=[]] - Array of optional variables for processing.
 * @returns {Array} Processed dataset with added group calculations.
 */
const CountSubGroups = (data, optionalVariables = []) => {
  optionalVariables.forEach((variableArray) => {
    const [variable, , NAMessage, isNumeric, customGroups] = variableArray;

    if (isNumeric && customGroups.length > 0) {
      data.forEach((item) => {
        const numericValue = parseInt(item[variable], 10);
        if (!isNaN(numericValue)) {
          for (const [lower, upper, group] of customGroups) {
            if (numericValue >= lower && numericValue <= upper) {
              item[variable] = group;
              break;
            }
          }
        } else {
          item[variable] = NAMessage;
        }
      });
    }
  });

  return tidy(
    data,
    rename({
      ...optionalVariables.reduce((acc, [variable, varName]) => {
        acc[variable] = varName;
        return acc;
      }, {})
    }),
    replaceNully({
      ...optionalVariables.reduce((acc, [variable, , NAMessage]) => {
        acc[variable] = NAMessage;
        return acc;
      }, {})
    }),
    groupBy(
      optionalVariables.map(([variable, varName]) => varName),
      [count(optionalVariables[0][1], { name: 'value' })]
    )
  );
};


/**
 * Get summary statistics for a column.
 *
 * @version 1.0
 * 
 * @param {Array} column - Column of data.
 * @param {number} [decimalPlaces=2] - Number of decimal places for rounding.
 * @returns {Object} Summary statistics for the column.
 */
function getColumnSummaryStats(column, decimalPlaces = 2) {
  const isNumerical = column.every(item => typeof item === 'number');

  if (isNumerical) {
    const mean = column.reduce((acc, value) => acc + value, 0) / column.length;
    const roundedMean = parseFloat(mean.toFixed(decimalPlaces));
    const sortedData = [...column].sort((a, b) => a - b);
    const middle = Math.floor(column.length / 2);
    const median = column.length % 2 === 1
      ? sortedData[middle]
      : (sortedData[middle - 1] + sortedData[middle]) / 2;

    return {
      Mean: roundedMean,
      Median: median,
      Min: Math.min(...column),
      Max: Math.max(...column),
    };
  } else {
    const counts = {};
    column.forEach(item => {
      counts[item] = (counts[item] || 0) + 1;
    });

    let mode = null;
    let maxCount = 0;
    for (const key in counts) {
      if (counts[key] > maxCount) {
        mode = key;
        maxCount = counts[key];
      }
    }

    const total = column.length;
    const percentages = {};
    for (const key in counts) {
      percentages[key] = (counts[key] / total) * 100;
    }

    return {
      Counts: counts,
      Mode: mode,
      Percentages: percentages,
    };
  }
}

export {getColumnSummaryStats, calculateAverage, calculateMedian, calculatePercentages, CountSubGroups };
